import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

interface ServerTimeData {
  datetime: string
  date: string
  time: string
  weekday: string
  timezone: string
  timezone_abbr: string
  utc_datetime: string
  timestamp: number
  formatted: string
  formatted_short: string
  simulation_active: boolean
}

interface UseServerTimeOptions {
  /** 
   * Optional timezone to request (e.g., "America/Los_Angeles")
   * If not provided, server will use user's profile timezone or server default
   */
  timezone?: string
  /** Refresh interval in milliseconds (default: 60000 = 1 minute) */
  refreshInterval?: number
}

interface UseServerTimeReturn {
  /** Server date as a Date object (in user's timezone) */
  serverDate: Date
  /** Server date string in yyyy-MM-dd format */
  serverDateString: string
  /** Full server time data including timezone info */
  serverTime: ServerTimeData | null
  /** Whether initial load is in progress */
  isLoading: boolean
  /** Error message if fetch failed */
  error: string | null
  /** User's timezone from server response */
  userTimezone: string
  /** Refresh server time */
  refresh: () => Promise<void>
  /** Format a date in user's timezone */
  formatInUserTimezone: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
}

/**
 * Hook to fetch and use server time (NTP-synced) instead of client time.
 * 
 * The server returns time in the user's configured timezone (from their profile),
 * ensuring all clients see consistent time based on NTP-synced server time.
 * 
 * Features:
 * - Server time is NTP-synced via horton-timemachine
 * - Returns time in user's configured timezone
 * - Falls back to client time if server is unavailable
 * - Auto-refreshes to stay in sync
 * 
 * @param options Configuration options
 * @returns Server time data and utilities
 *
 * The fetching implementation lives in `useServerTimeSource` below and is
 * mounted ONCE, by the app-root provider; `useServerTime` (further down) is
 * the unchanged public hook every caller keeps using.
 */
/**
 * The ONE server clock, published by the app-root provider.
 *
 * Defect fix 2026-08-29: every caller opened its own /api/current-time poller
 * — on /today that is the Sidebar (via useTimeOfDay) and TodayFocus, plus
 * ScheduleV2 / ReviewSchedule on their routes. The hook's public shape is
 * unchanged. ServerPacificTime asks for a specific timezone, which is a
 * different request, and keeps its own.
 */
export const ServerTimeContext = createContext<UseServerTimeReturn | null>(null)

/**
 * The fetching implementation. Mounted once by the provider; `enabled` is
 * false for the fallback instance inside `useServerTime` when the shared clock
 * is being used, so no second poller starts.
 */
export function useServerTimeSource(
  options: UseServerTimeOptions = {},
  enabled: boolean = true,
): UseServerTimeReturn {
  const { 
    timezone, 
    refreshInterval = 60000
  } = options
  
  const [serverTime, setServerTime] = useState<ServerTimeData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchServerTime = useCallback(async () => {
    try {
      // Build query params - if timezone specified, use it; otherwise server uses user profile
      const params = new URLSearchParams()
      if (timezone) {
        params.append('timezone', timezone)
      }
      
      const queryString = params.toString()
      const url = queryString ? `/api/current-time?${queryString}` : '/api/current-time'
      
      const response = await fetch(url, { credentials: 'include' })
      if (response.ok) {
        const data = await response.json()
        setServerTime(data)
        setError(null)
      } else {
        throw new Error('Failed to fetch server time')
      }
    } catch (err) {
      console.warn('Could not fetch server time, using client time:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setIsLoading(false)
    }
  }, [timezone])

  useEffect(() => {
    if (!enabled) return
    fetchServerTime()
    
    // Refresh at configured interval to stay in sync
    const interval = setInterval(fetchServerTime, refreshInterval)
    return () => clearInterval(interval)
  }, [enabled, fetchServerTime, refreshInterval])

  // Compute the server date, falling back to client date if unavailable.
  // Defect fix 2026-08-29: this built a FRESH Date on every render, so every
  // caller that put `serverDate` in a dependency array (TodayFocus:
  // getEndDate -> fetchTodayItems -> fetchTodayViewFromOrchestrator -> the
  // 5-minute auto-refresh effect) rebuilt those callbacks on every render and
  // cleared/recreated its interval before it could ever fire. Keyed on the
  // server datetime STRING the object is stable between polls.
  const serverDate = useMemo(
    () => (serverTime ? new Date(serverTime.datetime) : new Date()),
    [serverTime?.datetime],
  )
  
  const serverDateString = serverTime?.date || new Date().toISOString().split('T')[0]
  
  // User's timezone from server response (or browser default)
  const userTimezone = serverTime?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  
  // Format a date in user's timezone
  const formatInUserTimezone = useCallback((date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const d = typeof date === 'string' ? new Date(date) : date
    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone: userTimezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }
    return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
  }, [userTimezone])

  return useMemo(() => ({
    serverDate,
    serverDateString,
    serverTime,
    isLoading,
    error,
    userTimezone,
    refresh: fetchServerTime,
    formatInUserTimezone
  }), [serverDate, serverDateString, serverTime, isLoading, error, userTimezone, fetchServerTime, formatInUserTimezone])
}

/**
 * Public hook — unchanged call shape. Reads the app-root clock when one is
 * mounted. A caller that asks for a SPECIFIC timezone is asking a different
 * question of a different URL, so it keeps its own fetch; everyone else shares
 * the one poll. A caller asking for a *slower* refresh than the shared clock
 * still shares it: more-recent time costs no extra request.
 */
export function useServerTime(options: UseServerTimeOptions = {}): UseServerTimeReturn {
  const shared = useContext(ServerTimeContext)
  const useShared = shared !== null && !options.timezone
  const local = useServerTimeSource(options, !useShared)
  return useShared ? (shared as UseServerTimeReturn) : local
}

/**
 * Get server date string (for use outside of React components).
 * Returns a promise that resolves to the server date string in yyyy-MM-dd format.
 * 
 * @param timezone Optional timezone to request (uses user profile timezone if not specified)
 */
export async function getServerDateString(timezone?: string): Promise<string> {
  try {
    const params = new URLSearchParams()
    if (timezone) {
      params.append('timezone', timezone)
    }
    
    const queryString = params.toString()
    const url = queryString ? `/api/current-time?${queryString}` : '/api/current-time'
    
    const response = await fetch(url, { credentials: 'include' })
    if (response.ok) {
      const data = await response.json()
      return data.date
    }
  } catch (err) {
    console.warn('Could not fetch server time:', err)
  }
  // Fallback to client date
  return new Date().toISOString().split('T')[0]
}

/**
 * Get full server time data (for use outside of React components).
 * Returns a promise that resolves to the full server time data.
 * 
 * @param timezone Optional timezone to request (uses user profile timezone if not specified)
 */
export async function getServerTime(timezone?: string): Promise<ServerTimeData | null> {
  try {
    const params = new URLSearchParams()
    if (timezone) {
      params.append('timezone', timezone)
    }
    
    const queryString = params.toString()
    const url = queryString ? `/api/current-time?${queryString}` : '/api/current-time'
    
    const response = await fetch(url, { credentials: 'include' })
    if (response.ok) {
      return await response.json()
    }
  } catch (err) {
    console.warn('Could not fetch server time:', err)
  }
  return null
}

/**
 * Format a date in a specific timezone
 * 
 * @param date Date to format
 * @param timezone IANA timezone string (e.g., "America/Los_Angeles")
 * @param options Intl.DateTimeFormat options
 */
export function formatDateInTimezone(
  date: Date | string, 
  timezone: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(d)
}
